<template>
  <div class="event-card">
    <div class="event-card-image-container" @click="openVideo" style="cursor: pointer; position: relative;">
      <img :src="event.image || event.thumbnail" :alt="event.title" class="event-card-image" />
      <div v-if="event.video || event.video_type" class="event-card-play-overlay">
        <i class="fa fa-play-circle play-icon"></i>
      </div>
    </div>
    <div class="event-card-body">
      <div class="event-card-meta">
        <span class="meta-item">
          <i class="fa fa-calendar meta-icon"></i>
          <span>{{ formatDate(event.date) }}</span>
        </span>
        <span class="meta-dot">•</span>
        <span class="meta-item">
          <i class="fa fa-map-marker meta-icon"></i>
          <span>{{ event.location }}</span>
        </span>
      </div>
      <div class="event-card-title">{{ event.title }}</div>
      <p class="event-card-desc" style="margin-bottom: 10px;">{{ event.description }}</p>
      <router-link :to="`/event/${event.id}`" class="orange-btn">
        View Details <span class="arrow">→</span>
      </router-link>
    </div>

    <!-- Video Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeVideo">
      <div class="modal-header-bar">
        <span class="modal-title">Upcoming Events</span>
        <button class="modal-close" @click="closeVideo">Close &times;</button>
      </div>
      <div class="modal-video-center">
        <div class="video-wrapper">
          <iframe
            v-if="videoEmbedUrl"
            :key="'iframe-' + videoKey"
            :src="videoEmbedUrl"
            frameborder="0"
            allow="autoplay; encrypted-media"
            allowfullscreen
            class="modal-video"
          ></iframe>
          <video
            v-else-if="isDirectVideo"
            :key="'video-' + videoKey"
            :src="event.video"
            controls
            autoplay
            class="modal-video"
          ></video>
          <div v-else class="text-center py-5" style="color: #fff;">Video format not supported.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { defineProps } from 'vue'

const props = defineProps({
  event: {
    type: Object,
    required: true
  }
})

const showModal = ref(false)
const videoKey = ref(0)

const openVideo = () => {
  if (props.event.video || props.event.video_type) {
    videoKey.value++ // force re-mount video/iframe
    showModal.value = true
  }
}
const closeVideo = () => {
  showModal.value = false
  // videoKey will increment next open, so video is reset
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

const isYouTube = computed(() => {
  return props.event.video && /youtube\.com|youtu\.be/.test(props.event.video)
})
const isVimeo = computed(() => {
  return props.event.video && /vimeo\.com/.test(props.event.video)
})
const isDirectVideo = computed(() => {
  return props.event.video && !isYouTube.value && !isVimeo.value
})

const videoEmbedUrl = computed(() => {
  if (!props.event.video) return ''
  if (isYouTube.value) {
    // Extract YouTube video ID
    const match = props.event.video.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([\w-]{11})/)
    const id = match ? match[1] : ''
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : ''
  }
  if (isVimeo.value) {
    // Extract Vimeo video ID
    const match = props.event.video.match(/vimeo\.com\/(\d+)/)
    const id = match ? match[1] : ''
    return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : ''
  }
  return ''
})
</script>

<style scoped>
.event-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(44, 62, 80, 0.10);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s;
  margin-bottom: 24px;
}
.event-card:hover {
  box-shadow: 0 8px 32px rgba(44, 62, 80, 0.16);
}
.event-card-image-container {
  height: 200px;
  overflow: hidden;
  position: relative;
}
.event-card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}
.event-card-image-container:hover .event-card-image {
  transform: scale(1.05);
}
.event-card-play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.35);
}
.play-icon {
  font-size: 60px;
  color: #fff;
  opacity: 0.9;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
}
.event-card-body {
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}
.event-card-meta {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 10px;
}
.meta-item {
  display: flex;
  align-items: center;
  background: #f3f4f6;
  border-radius: 16px;
  padding: 3px 10px 3px 7px;
  gap: 5px;
}
.meta-icon {
  font-size: 1em;
  color: #2563eb;
  margin-right: 4px;
}
.meta-dot {
  margin: 0 6px;
  color: #bdbdbd;
  font-size: 1.1em;
}
.event-card-title {
  font-size: 20px;
  font-weight: bold;
  color: #111827;
  margin-bottom: 12px;
  transition: color 0.2s;
  text-decoration: none;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 48px;
  line-height: 24px;
}
.event-card-desc {
  color: #4b5563;
  margin-bottom: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 60px;
  line-height: 20px;
}
.event-detail-btn {
  margin-top: 18px;
  /* Remove background, color, border, etc. so .orange-btn takes over */
  background: none;
  color: inherit;
  border: none;
  box-shadow: none;
  padding: 0;
}
.event-detail-btn:hover {
  background: #1e40af;
  color: #fff;
  text-decoration: none;
  transform: translateY(-2px);
}
.event-detail-btn .arrow {
  margin-left: 8px;
  font-size: 1.1em;
  transition: transform 0.3s ease;
}
.event-detail-btn:hover .arrow {
  transform: translateX(4px);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.85);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.modal-header-bar {
  width: 100vw;
  max-width: 900px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 8px;
  padding: 0 24px;
}
.modal-title {
  font-size: 1.2rem;
  font-weight: 500;
  letter-spacing: 0.5px;
}
.modal-close {
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  margin-left: 16px;
  font-weight: 400;
}
.modal-video-center {
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
}
.video-wrapper {
  background: #000;
  border-radius: 20px;
  overflow: hidden;
  max-width: 900px;
  width: 90vw;
  box-shadow: 0 8px 32px rgba(44, 62, 80, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-video {
  width: 100%;
  height: 55vw;
  max-width: 900px;
  max-height: 60vh;
  border-radius: 20px;
  background: #000;
  display: block;
}
@media (max-width: 900px) {
  .modal-header-bar {
    max-width: 100vw;
    padding: 0 8px;
  }
  .video-wrapper {
    max-width: 100vw;
    border-radius: 12px;
  }
  .modal-video {
    border-radius: 12px;
    height: 50vw;
    max-height: 40vh;
  }
}
@media (max-width: 600px) {
  .modal-header-bar {
    font-size: 1rem;
    padding: 0 4px;
  }
  .modal-title {
    font-size: 1rem;
  }
  .modal-close {
    font-size: 1.5rem;
  }
  .video-wrapper {
    border-radius: 8px;
  }
  .modal-video {
    border-radius: 8px;
    height: 40vw;
    max-height: 30vh;
  }
}
</style> 